import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ExternalLink, FileText, Image, Video, Music, Sparkles, Loader2, Download, Plus, Highlighter, Home, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import AppShell from '@/components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOURCE_TYPE_ICONS = {
  document: FileText,
  image: Image,
  video: Video,
  audio: Music,
  artifact: FileText,
  website: ExternalLink
};

const SOURCE_TYPE_COLORS = {
  document: 'bg-blue-100 text-blue-800',
  image: 'bg-green-100 text-green-800',
  video: 'bg-purple-100 text-purple-800',
  audio: 'bg-orange-100 text-orange-800',
  artifact: 'bg-[#FBF6E8] text-[#14213D]',
  website: 'bg-slate-100 text-slate-800'
};

export default function PrimarySources() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [unit, setUnit] = useState(null);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [excerptLoadingIdx, setExcerptLoadingIdx] = useState(null);
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    fetchData();
  }, [unitId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [unitRes, sourcesRes] = await Promise.all([
        axios.get(`${API}/units/${unitId}`),
        axios.get(`${API}/primary-sources/${unitId}`)
      ]);
      
      setUnit(unitRes.data);
      setSources(sourcesRes.data.sources || []);
    } catch (error) {
      toast.error('Failed to load primary sources');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const response = await axios.post(`${API}/primary-sources/${unitId}/generate`);
      setSources(response.data.sources || []);
      toast.success('Primary sources generated successfully!');
    } catch (error) {
      toast.error('Failed to generate primary sources');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateExcerpt = async (idx) => {
    try {
      setExcerptLoadingIdx(idx);
      const res = await axios.post(
        `${API}/primary-sources/${unitId}/generate-excerpt/${idx}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSources((prev) => prev.map((s, i) => (i === idx ? { ...s, full_text: res.data.full_text } : s)));
      toast.success(`Excerpt generated (${res.data.length} chars)`);
    } catch (error) {
      const msg = error?.response?.data?.detail || error.message;
      toast.error(`Failed to generate excerpt: ${msg}`);
    } finally {
      setExcerptLoadingIdx(null);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/primary-sources/${unitId}/export`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `primary_sources_${unit.title.replace(/ /g, '_')}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Primary sources exported!');
    } catch (error) {
      toast.error('Failed to export primary sources');
    }
  };

  const filteredSources = selectedType === 'all' 
    ? sources 
    : sources.filter(s => s.type === selectedType);

  const sourceTypes = ['all', ...new Set(sources.map(s => s.type))];

  if (loading) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#14213D] mx-auto" />
            <p className="mt-4 text-sm text-[#5A6577]">Loading primary sources...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/unit/${unitId}`)}
          className="mb-4 text-[#5A6577] hover:text-[#14213D]"
          data-testid="back-to-unit-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Unit
        </Button>

        <div className="mb-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Primary Sources</p>
          <h1 className="font-serif-display text-4xl text-[#14213D] tracking-tight mb-2">
            {unit?.title}
          </h1>
          <p className="text-sm text-[#5A6577]">
            Curated primary sources from archives, museums, and libraries
          </p>
        </div>

        {sources.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="h-16 w-16 text-[#CBD5E0] mx-auto mb-4" />
            <h3 className="text-xl font-serif text-[#14213D] mb-2">
              No Primary Sources Yet
            </h3>
            <p className="text-[#5A6577] mb-6">
              Generate curated primary source links from major archives and collections
            </p>
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="btn-primary"
              data-testid="generate-primary-sources-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Primary Sources
                </>
              )}
            </Button>
          </Card>
        ) : (
          <>
            {/* Header Actions */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex gap-2">
                {sourceTypes.map((type) => (
                  <Button
                    key={type}
                    variant={selectedType === type ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedType(type)}
                    className={selectedType === type ? 'bg-[#14213D]' : ''}
                  >
                    {type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1)}
                    {type !== 'all' && ` (${sources.filter(s => s.type === type).length})`}
                  </Button>
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGenerate}
                  disabled={generating}
                  data-testid="regenerate-btn"
                >
                  {generating ? (
                    <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3 mr-2" />
                  )}
                  Regenerate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  data-testid="export-sources-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>

            {/* Sources Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="sources-grid">
              {filteredSources.map((source, index) => {
                const Icon = SOURCE_TYPE_ICONS[source.type] || FileText;
                const hasExcerpt = Boolean(source.full_text && source.full_text.length > 0);
                return (
                  <Card key={`${source.title}-${index}`} className="p-6 hover:shadow-lg transition-shadow" data-testid={`source-${source.title.slice(0,20).replace(/\s+/g,'-')}`}>
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${SOURCE_TYPE_COLORS[source.type] || SOURCE_TYPE_COLORS.document}`}>
                        <Icon className="h-6 w-6" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-serif font-normal text-lg text-[#14213D] leading-tight">
                            {source.title}
                          </h3>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <Badge variant="outline">{source.type}</Badge>
                            {hasExcerpt && (
                              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[11px] gap-1" data-testid={`excerpt-ready-${index}`}>
                                <CheckCircle2 className="h-3 w-3" /> Annotation-ready
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <p className="text-sm text-[#5A6577] mb-3 leading-relaxed">
                          {source.description}
                        </p>

                        {source.source_organization && (
                          <p className="text-xs text-[#5A6577] mb-2">
                            <strong>Source:</strong> {source.source_organization}
                          </p>
                        )}

                        {source.date_created && (
                          <p className="text-xs text-[#5A6577] mb-3">
                            <strong>Date:</strong> {source.date_created}
                          </p>
                        )}

                        <div className="flex items-center gap-2 mb-4 flex-wrap">
                          <a
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm text-[#14213D] hover:text-slate-900 font-medium"
                          >
                            <ExternalLink className="h-4 w-4" />
                            View Source
                          </a>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/annotate/${unitId}/${index}`);
                            }}
                            data-testid={`annotate-source-${index}`}
                          >
                            <Highlighter className="h-4 w-4 mr-1" />
                            Annotate
                          </Button>
                          {isAdmin && !hasExcerpt && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleGenerateExcerpt(index)}
                              disabled={excerptLoadingIdx === index}
                              className="border-yellow-500 text-[#14213D] hover:bg-[#FBF6E8]"
                              data-testid={`generate-excerpt-${index}`}
                              title="Generate an annotation-ready excerpt (AI, public-domain style)"
                            >
                              {excerptLoadingIdx === index ? (
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <Sparkles className="h-4 w-4 mr-1" />
                              )}
                              Generate Excerpt
                            </Button>
                          )}
                        </div>

                        {source.analysis_questions && source.analysis_questions.length > 0 && (
                          <div className="mt-4 p-3 bg-[#FBF6E8] border border-[#D4AF37]/40 rounded-lg">
                            <p className="text-xs font-semibold text-[#14213D] mb-2">Analysis Questions:</p>
                            <ul className="text-xs text-[#2D3748] space-y-1">
                              {source.analysis_questions.map((q) => (
                                <li key={q.slice(0, 40)} className="flex gap-2">
                                  <span className="text-yellow-600">•</span>
                                  <span>{q}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {source.keywords && source.keywords.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1">
                            {source.keywords.map((keyword) => (
                              <span
                                key={keyword}
                                className="text-xs bg-[#F9F8F4] text-[#5A6577] px-2 py-1 rounded"
                              >
                                {keyword}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            <div className="mt-8 text-center text-sm text-[#5A6577]">
              Showing {filteredSources.length} of {sources.length} primary sources
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
